<script setup lang="ts">
import { influencerShareUrl, shareInfluencerUrl } from '../../utils/influencerShare'

const route = useRoute()
const getRouteBaseName = useRouteBaseName()
const { t } = useI18n()
const { toast } = useToast()
const { isMobileOrTablet } = useDevice()
const { codes } = useInfluencerShareCodes()
const selectedCode = ref('')
const sharing = ref(false)
const manualLink = ref('')

// Account and checkout URLs can contain private order or authentication details.
const publicPage = computed(() => !/^(?:account|auth|checkout|preview|track-order|delete-account|forgot-password|wishlist)(?:-|$)/.test(String(getRouteBaseName(route) ?? '')))
const code = computed(() => codes.value.find(item => item.couponCode === selectedCode.value) ?? codes.value[0])

watch([() => route.fullPath, code], () => {
  manualLink.value = ''
})

async function share() {
  if (!code.value || sharing.value)
    return

  sharing.value = true
  const url = influencerShareUrl(window.location.href, code.value.couponCode)
  try {
    const result = await shareInfluencerUrl(url, isMobileOrTablet)
    if (result === 'copied')
      toast({ variant: 'success', title: t('general.copied') })
    manualLink.value = ''
  }
  catch {
    manualLink.value = url
    toast({ variant: 'destructive', title: t('general.copy_failed') })
  }
  finally {
    sharing.value = false
  }
}
</script>

<template>
  <div
    v-if="publicPage && code"
    class="fixed bottom-[calc(env(safe-area-inset-bottom)+96px)] left-4 z-40 max-w-[calc(100vw-32px)] flex flex-col items-start gap-2 md:bottom-6 md:left-6"
    data-testid="influencer-share"
  >
    <div class="flex items-center overflow-hidden border border-white/20 rounded-full bg-black text-white shadow-lg">
      <UiSelect v-if="codes.length > 1" :model-value="code.couponCode" @update:model-value="selectedCode = String($event)">
        <UiSelectTrigger :aria-label="t('referral.your_coupon')" class="h-11 max-w-40 w-auto gap-2 border-0 border-r border-white/25 rounded-none bg-transparent pl-4 text-xs text-white focus:ring-inset">
          <UiSelectValue />
        </UiSelectTrigger>
        <UiSelectContent>
          <UiSelectItem v-for="item in codes" :key="item.couponCode" :value="item.couponCode">
            {{ item.couponCode }}
          </UiSelectItem>
        </UiSelectContent>
      </UiSelect>
      <UiButton type="button" :disabled="sharing" class="h-11 gap-2 rounded-full bg-black px-4 text-white hover:bg-[#222]" @click="share">
        <Icon name="lucide:share-2" class="size-4" aria-hidden="true" />
        {{ t('general.share') }}
      </UiButton>
    </div>
    <UiInput
      v-if="manualLink"
      :model-value="manualLink"
      :aria-label="t('general.share')"
      readonly
      class="w-72 bg-white shadow-lg"
      @focus="($event.target as HTMLInputElement).select()"
    />
  </div>
</template>
